import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import { PERMIT_TO_WORK_CATEGORIES, PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS } from "../../../constants/common"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import useAuthStore from "../../../store/Auth"
import { APP_ROUTES } from "../../../routes"
import { useCallback, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { fileToBase64, formattedDateTime } from "../../../lib/functions"
import { useAddUpdatePermitToWork } from "../../../hooks/permit/mutation"

interface FormData {
    affectedService: string;
    permitStartDate: string;
    permitEndDate: string;
    workCategory: string;
    highRiskWorkCategory?: string;
    scopeOfWork: string;
    workType: string;
    floorNo: string;
    officeNumber: string;
    uploadWorkPlan?: any;
    uploadMethodStatement?: any;
    uploadRiskAssessment?: any;
}

interface ActionButtonGroupProps {
    permitToWork?: {
        n_PermitToWorkID: number;
        n_ApprovalStatus: number;
        n_PermitNextApproverRoleID: number;
        permitEndDate?: string;
    }
    handleSubmit: any;
    checkedBuildingIds: number[];
    safetyRequirementsChecklist: Record<string, boolean>;
    isQualifiedPersonnelOnly: boolean;
    isSafetyBriefingConducted: boolean;
    isAgreeStopWorkIfConditionsChange: boolean;
    isPermitApprovedByNextApprover: boolean;
    isFieldsDisabled: boolean;
}

const ActionButtonGroup = ({
    permitToWork,
    handleSubmit,
    checkedBuildingIds,
    safetyRequirementsChecklist,
    isQualifiedPersonnelOnly,
    isSafetyBriefingConducted,
    isAgreeStopWorkIfConditionsChange,
    isPermitApprovedByNextApprover,
    isFieldsDisabled,
}: ActionButtonGroupProps) => {
    const { t } = useTranslation()

    const location = useLocation();

    const { user } = useAuthStore()

    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);
    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const {
        mutateAsync: AddUpdatePermitToWork,
        isPending: isAddUpdatePermitToWorkLoading,
    } = useAddUpdatePermitToWork();

    const isApproveDisabled = useMemo(
        () =>
            permitToWork?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            permitToWork?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            permitToWork?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            permitToWork?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (permitToWork?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== permitToWork?.n_PermitNextApproverRoleID),
        [
            permitToWork?.n_ApprovalStatus,
            permitToWork?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ],
    );

    const isRejectDisabled = useMemo(
        () =>
            permitToWork?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            permitToWork?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            permitToWork?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [permitToWork?.n_ApprovalStatus],
    );


    const isApprovePermitPage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_PERMIT_TO_WORK,
        [location.pathname],
    );

    const onApprove = useCallback(
        async (data: FormData) => {
            if (isApproveDisabled) return;

            if (new Date(data.permitEndDate) <= new Date()) {
                toast.error(t("permitExpiredCannotApprove"));
            } else if (
                new Date(data.permitEndDate) <= new Date(data.permitStartDate)
            ) {
                toast.error(t("permitDateError"));
            } else {
                setIsApprovePermitModalOpen(true);
            }
        },
        [isApproveDisabled, t],
    );

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        if (
            permitToWork?.permitEndDate &&
            new Date(permitToWork?.permitEndDate) <= new Date()
        ) {
            toast.error(t("permitExpiredCannotReject"));
            return;
        }

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, permitToWork?.permitEndDate, t]);

    const onSubmit = useCallback(
        async (data: FormData) => {
            if (checkedBuildingIds.length === 0) {
                toast.error(t("selectAreasToAccess"));
                return;
            }

            try {
                const body = {
                    n_PermitToWorkID: key ? Number(key) : 0,

                    n_AffectedService: Number(data.affectedService),
                    dt_PermitStartDate: formattedDateTime(new Date(data.permitStartDate)),
                    dt_PermitEndDate: formattedDateTime(new Date(data.permitEndDate)),
                    n_WorkCategoryCode: Number(data.workCategory),
                    n_HighRiskWorkCategoryCode:
                        data.workCategory === PERMIT_TO_WORK_CATEGORIES.HIGH_RISK_WORKS
                            ? Number(data.highRiskWorkCategory)
                            : 0,
                    n_WorkTypeCode: Number(data.workType),

                    s_FloorNo: data.floorNo,
                    s_OfficeNo: data.officeNumber,

                    buildings: checkedBuildingIds,

                    s_ScopeOfWork: data.scopeOfWork,

                    selections: Object.keys(safetyRequirementsChecklist).map((key) => ({
                        n_ItemID: Number(key),
                        b_IsChecked: safetyRequirementsChecklist[key],
                    })),

                    s_WorkPlanBase64: data.uploadWorkPlan
                        ? await fileToBase64(data.uploadWorkPlan)
                        : "",
                    s_WorkPlanContentType: data.uploadWorkPlan
                        ? data.uploadWorkPlan.type
                        : "",

                    s_MethodStatementBase64: data.uploadMethodStatement
                        ? await fileToBase64(data.uploadMethodStatement)
                        : "",
                    s_MethodStatementContentType: data.uploadMethodStatement
                        ? data.uploadMethodStatement.type
                        : "",

                    s_RiskAssessmentBase64:
                        data.workCategory === PERMIT_TO_WORK_CATEGORIES.HIGH_RISK_WORKS &&
                            data.uploadRiskAssessment
                            ? await fileToBase64(data.uploadRiskAssessment)
                            : "",
                    s_RiskAssessmentContentType:
                        data.workCategory === PERMIT_TO_WORK_CATEGORIES.HIGH_RISK_WORKS &&
                            data.uploadRiskAssessment
                            ? data.uploadRiskAssessment.type
                            : "",

                    n_ApprovalStatus: STATUS_IDS.PENDING,
                };

                await AddUpdatePermitToWork(body);

                toast.success(
                    t(!key ? "permitRequestedSuccessfully" : "permitUpdatedSuccessfully"),
                );

                setTimeout(() => {
                    navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
                }, 700);
            } catch (error) {
                toast.error(t("somethingWentWrong"));
            }
        },
        [AddUpdatePermitToWork, checkedBuildingIds, key, navigate, safetyRequirementsChecklist, t],
    );

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePermitPage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={isAddUpdatePermitToWorkLoading}
                                    disabled={
                                        !isQualifiedPersonnelOnly ||
                                        !isSafetyBriefingConducted ||
                                        !isAgreeStopWorkIfConditionsChange ||
                                        isAddUpdatePermitToWorkLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}

            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdatePermitToWorkLoading}
                            disabled={
                                !isQualifiedPersonnelOnly ||
                                !isSafetyBriefingConducted ||
                                !isAgreeStopWorkIfConditionsChange ||
                                isAddUpdatePermitToWorkLoading
                            }
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.PERMIT_TO_WORK}
                permitId={permitToWork?.n_PermitToWorkID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.PERMIT_TO_WORK}
                permitId={permitToWork?.n_PermitToWorkID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default ActionButtonGroup
